import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ResetShippingAddressFormValidation";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import { TextFieldProps } from "@insite/mobius/TextField";
import validators from "@tSurya/Common/Utilities/validations";
import validateShippingAddressForm from "@tSurya/Handlers/validateShippingAddressForm";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@tSurya/Overrides/Components/CustomerAddressFormFields";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: ShippingAddressFormStyles;
}

const mapStateToProps = ({
    pages: {
        checkoutShipping: { shippingAddressFormState },
    },
}: ApplicationState) => ({
    shippingAddressFormState,
});

const mapDispatchToProps = {
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
    setFormErrors: (formErrors: { [key: string]: React.ReactNode }) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddressFormErrors",
            formErrors,
        });
    },
    validateForm: makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    ),
    resetShippingAddressFormValidation,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

type State = {
    styles: ShippingAddressFormStyles;
    addressType: string;
    dockdoor: string;
};

export interface ShippingAddressFormStyles {
    formFields?: CustomerAddressFormFieldStyles;
}

export const oneTimeAddressFormStyles: ShippingAddressFormStyles = {};

const styles = oneTimeAddressFormStyles;

class ShippingAddressForm extends React.Component<Props, State> {
    static contextType = CheckoutShippingFormContext;

    context!: React.ContextType<typeof CheckoutShippingFormContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            styles: mergeToNew(styles, this.props.extendedStyles),
            addressType: "",
            dockdoor: "",
        };
    }

    componentDidMount() {
        const { shippingAddressFormState, fieldDisplay, validateForm } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        const { validation } = address;
        if (!validation) {
            return;
        }

        const shouldValidate =
            validation.firstName?.isDisabled === false ||
            validation.lastName?.isDisabled === false ||
            validation.companyName?.isDisabled === false ||
            validation.attention?.isDisabled === false ||
            validation.address1?.isDisabled === false ||
            validation.address2?.isDisabled === false ||
            validation.address3?.isDisabled === false ||
            validation.address4?.isDisabled === false ||
            validation.city?.isDisabled === false ||
            validation.state?.isDisabled === false ||
            validation.country?.isDisabled === false ||
            validation.postalCode?.isDisabled === false ||
            validation.phone?.isDisabled === false ||
            validation.email?.isDisabled === false ||
            validation.fax?.isDisabled === false;
        if (shouldValidate) {
            this.context.validators.shippingAddress = () =>
                validateForm({
                    address,
                    validation,
                    fieldDisplay,
                });
            this.props.resetShippingAddressFormValidation();
        }
    }

    componentWillUnmount() {
        this.context.validators.shippingAddress = undefined;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { shippingAddressFormState: prevShippingAddressFormState, fieldDisplay: prevFieldDisplay } = prevProps;
        const { shippingAddressFormState, fieldDisplay, validateForm } = this.props;
        if (prevShippingAddressFormState !== shippingAddressFormState && shippingAddressFormState) {
            const { address } = shippingAddressFormState;
            const { validation } = address;
            if (
                (!prevShippingAddressFormState ||
                    prevShippingAddressFormState.address !== address ||
                    prevShippingAddressFormState.address.validation !== validation ||
                    prevFieldDisplay !== fieldDisplay) &&
                validation
            ) {
                const shouldValidate =
                    validation.firstName?.isDisabled === false ||
                    validation.lastName?.isDisabled === false ||
                    validation.companyName?.isDisabled === false ||
                    validation.attention?.isDisabled === false ||
                    validation.address1?.isDisabled === false ||
                    validation.address2?.isDisabled === false ||
                    validation.address3?.isDisabled === false ||
                    validation.address4?.isDisabled === false ||
                    validation.city?.isDisabled === false ||
                    validation.state?.isDisabled === false ||
                    validation.country?.isDisabled === false ||
                    validation.postalCode?.isDisabled === false ||
                    validation.phone?.isDisabled === false ||
                    validation.email?.isDisabled === false ||
                    validation.fax?.isDisabled === false;
                if (shouldValidate) {
                    this.context.validators.shippingAddress = () =>
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });

                    if (this.context.formSubmitAttempted) {
                        validateForm({
                            address,
                            validation,
                            fieldDisplay,
                        });
                    }
                } else {
                    this.context.validators.shippingAddress = undefined;
                }
            }
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;
        const { shippingAddressFormState, setAddress, setFormErrors } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        let errorMessage = "";

        if (propertyName === "phone" || propertyName === "fax") {
            if (propertyName === "phone" && target.value.trim().length > 0 && !validatePhone(target.value)) {
                errorMessage = siteMessage("Phone number must contain at least 10 digits.") as string;
            }
            // Remove all non-digit characters
            const cleanedValue = target.value.replace(/\D/g, "");

            // Perform the formatting
            const formattedValue = cleanedValue.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");

            setAddress({
                ...address,
                [event.target.name]: formattedValue,
            });
        } else if (propertyName === "postalCode") {
            let maskValue = event.target.value.replace(/[^0-9]/g, "");
            if (maskValue.length > 5 && !maskValue.includes("-")) {
                maskValue = `${maskValue.slice(0, 5)}-${maskValue.slice(5)}`;
            }
            const isValid = maskValue.length === 5 || (maskValue.length === 10 && maskValue.includes("-"));
            const invalidErrorMessage = isValid === true ? "" : (siteMessage("Invalid PostalCode") as string);
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("Postal Code is required.") as string)
                    : invalidErrorMessage;

            setAddress({
                ...address,
                [event.target.name]: maskValue,
            });
        } else if (propertyName === "firstName") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("Website_Registration_FirstNameRequired") as string)
                    : !validators.name(target.value)
                    ? (siteMessage("CreateNewAccountInfo_InvalidFirstName") as string)
                    : "";
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        }else if (propertyName === "lastName") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("Website_Registration_LastNameRequired") as string)
                    : !validators.name(target.value)
                    ? (siteMessage("CreateNewAccountInfo_InvalidLastName") as string)
                    : "";
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        } else if (propertyName === "city") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("displayOrder_cityRequired") as string)
                    : !validators.city(target.value)
                    ? (siteMessage("City name can only contain letters, spaces, hyphens, and apostrophes.") as string)
                    : "";
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        } else if (propertyName === "email") {
            if (target.value.trim().length > 0 && !validateEmail(target.value)) {
                errorMessage = siteMessage("AddressInfo_EmailAddress_Validation") as string;
            }
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        } else if (propertyName === "address1") {
            if (target.value.trim().length === 0) {
                errorMessage = siteMessage("cutSewAndRug_streetOneRequired") as string;
            }
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        } else if (propertyName === "companyName") {
            errorMessage =
                !validators.company(target.value) && target.value.length > 0
                    ? (siteMessage("Address_invalidCompany") as string)
                    : "";
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        } else {
            setAddress({
                ...address,
                [event.target.name]: event.target.value,
            });
        }

        const newErrors = {
            ...(shippingAddressFormState.formErrors || {}),
            [propertyName]: errorMessage,
        };
        setFormErrors(newErrors);
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const { shippingAddressFormState, setAddress, setFormErrors } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;

        const errorMessage = !country ? siteMessage("Country is Required") : "";

        setAddress({
            ...address,
            country,
        });
        const newErrors = {
            ...(shippingAddressFormState.formErrors || {}),
            country: errorMessage,
        };
        setFormErrors(newErrors);
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const { shippingAddressFormState, setAddress, setFormErrors } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const error = !state ? siteMessage("OpenTradeAccount_stateRequired") : "";
        const { address } = shippingAddressFormState;
        setAddress({
            ...address,
            state,
        });
        const newErrors = {
            ...(shippingAddressFormState.formErrors || {}),
            state: error,
        };
        setFormErrors(newErrors);
    };

    handleAddressTypeChange = (event: React.ChangeEvent<HTMLSelectElement>, dockdoor: string) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;
        
        setAddress({
            ...address,
            properties: {
                residentialFlag: event.target.value,
                dockDoorRequired: dockdoor,
            },
        });

        this.setState({
            ...this.state,
            addressType: event.target.value,
        });
    };

    handleDockDoorChange = (event: React.ChangeEvent<HTMLSelectElement>, addressType: string) => {
        const { shippingAddressFormState, setAddress } = this.props;
        if (!shippingAddressFormState) {
            return;
        }
        const { address } = shippingAddressFormState;

        setAddress({
            ...address,
            properties: {
                residentialFlag: addressType,
                dockDoorRequired: event.target.value,
            },
        });

        this.setState({
            ...this.state,
            dockdoor: event.target.value,
        });
    };

    render() {
        const { shippingAddressFormState, countries, fieldDisplay } = this.props;
        const { styles } = this.state;
        if (!shippingAddressFormState) {
            return null;
        }
        const { address, formErrors } = shippingAddressFormState;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
            validation,
            properties,
        } = address;
        if (!validation) {
            return null;
        }

        return (
            <CustomerAddressFormFields
                firstName={firstName}
                lastName={lastName}
                attention={attention}
                companyName={companyName}
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                country={country}
                city={city}
                state={state}
                postalCode={postalCode}
                phone={phone}
                email={email}
                fax={fax}
                onChangeFirstName={this.handleInputChange}
                onChangeLastName={this.handleInputChange}
                onChangeAttention={this.handleInputChange}
                onChangeCompanyName={this.handleInputChange}
                onChangeAddress1={this.handleInputChange}
                onChangeAddress2={this.handleInputChange}
                onChangeAddress3={this.handleInputChange}
                onChangeAddress4={this.handleInputChange}
                onChangeCountry={this.handleCountryChange}
                onChangeState={this.handleStateChange}
                onChangeCity={this.handleInputChange}
                onChangePostalCode={this.handleInputChange}
                onChangePhone={this.handleInputChange}
                onChangeEmail={this.handleInputChange}
                onChangeFax={this.handleInputChange}
                firstNameError={formErrors.firstName}
                lastNameError={formErrors.lastName}
                attentionError={formErrors.attention}
                companyNameError={formErrors.companyName}
                address1Error={formErrors.address1}
                address2Error={formErrors.address2}
                address3Error={formErrors.address3}
                address4Error={formErrors.address4}
                countryError={formErrors.country}
                stateError={formErrors.state}
                cityError={formErrors.city}
                postalCodeError={formErrors.postalCode}
                phoneError={formErrors.phone}
                emailError={formErrors.email}
                faxError={formErrors.fax}
                countries={countries}
                validation={validation}
                fieldDisplay={fieldDisplay}
                extendedStyles={styles.formFields}
                addressType={properties.residentialFlag}
                dockdoor={properties.dockDoorRequired}
                onChangeAddressType={this.handleAddressTypeChange}
                onChangeDockDoor={this.handleDockDoorChange}
                addressTypeError={undefined}
                dockDoorError={undefined}
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddressForm);
